import { sql } from 'drizzle-orm'
import { z } from 'zod'
import { getD1 } from './d1-context'
import {
  PROJECT_ANCHOR_SQL,
  projectWorkSchema,
  projectWorkSql,
  projectWorkView,
} from './commons/project-work'
import type { ProjectWork } from './commons/project-work'
import { communityProject, projectRepositories } from '@/lib/community-projects'
import { readBoundedJson } from '@/lib/bounded-json'

export async function ssrProjectWork(
  slug: string,
  cursor?: string,
): Promise<ProjectWork | null> {
  const project = communityProject(slug)
  if (!project) return null
  if (cursor && !z.string().uuid().safeParse(cursor).success) return null
  const db = getD1()
  if (db) {
    const repositories = JSON.stringify(projectRepositories(project))
    const anchor = cursor
      ? await db.get<{ createdAt: string } | undefined>(
          sql`${sql.raw(PROJECT_ANCHOR_SQL.split('?')[0])}${repositories}${sql.raw(PROJECT_ANCHOR_SQL.split('?')[1])}${cursor}`,
        )
      : null
    if (cursor && !anchor) return null
    // Bind each positional value through Drizzle; contributed URLs never become SQL.
    const pieces = projectWorkSql(!!anchor).split('?')
    const values = [
      repositories,
      ...(anchor ? [anchor.createdAt, anchor.createdAt, cursor] : []),
    ]
    const query = sql.empty()
    for (let i = 0; i < pieces.length; i++) {
      query.append(sql.raw(pieces[i]))
      if (i < values.length) query.append(sql`${values[i]}`)
    }
    return projectWorkView(
      slug,
      await db.all<ProjectWork['items'][number]>(query),
    )
  }
  if (typeof window === 'undefined')
    throw new Error('Project work storage unavailable')
  const response = await fetch(
    `/api/agents/projects/${encodeURIComponent(slug)}/work${cursor ? '?cursor=' + encodeURIComponent(cursor) : ''}`,
    { credentials: 'omit', signal: AbortSignal.timeout(20000) },
  )
  if (response.status === 404) return null
  if (!response.ok) throw new Error('Project work unavailable')
  const data = projectWorkSchema.parse(await readBoundedJson(response, 100000))
  if (data.project !== slug) throw new Error('Project response mismatch')
  return data
}
